import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { ForkKnife, Flame, Lightning, Warning, ListChecks, Calculator } from "@phosphor-icons/react";

const ACTIVITY_LABEL = {
  sedentary: "Sedentary",
  light: "Light",
  moderate: "Moderate",
  active: "Active",
  very_active: "Very Active",
};
const DIETARY_LABEL = {
  none: "No restrictions",
  vegetarian: "Vegetarian",
  vegan: "Vegan",
  pescatarian: "Pescatarian",
  halal: "Halal",
  kosher: "Kosher",
  other: "Other",
};
const COOKTIME_LABEL = {
  "15min": "15 min/day",
  "30min": "30 min/day",
  "60min": "60+ min/day",
  shakes_only: "Shakes only",
};
const ALCOHOL_LABEL = {
  none: "None",
  "1-2_per_week": "1–2/week",
  "3-5_per_week": "3–5/week",
  "6+_per_week": "6+/week",
};

export default function ClientNutritionBrief({ clientId }) {
  const [brief, setBrief] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get(`/coach/clients/${clientId}/nutrition-brief`);
        setBrief(data);
      } catch (e) {
        setErr(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [clientId]);

  if (loading) {
    return <div className="id-card"><div className="id-label text-zinc-500 id-pulse">— Loading client brief —</div></div>;
  }
  if (err) {
    return <div className="id-card border-[#FF3333] text-[#FF3333] text-sm">{err}</div>;
  }
  if (!brief) return null;

  const { client, profile, latest_body_stats: body, goals, nutrition_prefs: prefs, wellness_14d_avg: well, estimates } = brief;
  const weightLoss = goals?.current_weight_kg && goals?.goal_weight_kg
    ? (goals.current_weight_kg - goals.goal_weight_kg).toFixed(1) : null;

  const allergies = (prefs?.allergies || "").trim();
  const avoids = (prefs?.foods_to_avoid || "").trim();

  return (
    <div className="id-card" data-testid="nutrition-brief">
      <div className="flex items-center gap-2 mb-5">
        <ForkKnife size={16} weight="bold" className="text-[#FF3333]" />
        <div className="id-label">Build the nutrition plan</div>
      </div>

      {/* Headline numbers */}
      <div className="grid grid-cols-2 gap-2 mb-6">
        <Cell label="Weight" value={body?.weight_kg ? `${body.weight_kg} kg` : "—"} hint={body?.date ? `as of ${new Date(body.date).toLocaleDateString()}` : ""} testid="brief-weight" />
        <Cell label="Goal" value={goals?.goal_weight_kg ? `${goals.goal_weight_kg} kg` : "—"} hint={weightLoss ? `${weightLoss} kg to lose` : ""} accent testid="brief-goal" />
        <Cell label="Height" value={profile?.height_cm ? `${profile.height_cm} cm` : "—"} testid="brief-height" />
        <Cell label="Sex" value={profile?.sex ? profile.sex.charAt(0).toUpperCase() + profile.sex.slice(1) : "—"} testid="brief-sex" />
      </div>

      {/* TDEE Estimate */}
      {estimates?.tdee ? (
        <div className="border border-[#FF3333] bg-[#FF3333]/5 p-4 mb-6" data-testid="brief-tdee">
          <div className="flex items-center gap-2 mb-3">
            <Calculator size={14} weight="bold" className="text-[#FF3333]" />
            <div className="id-label text-[#FF3333]">Calorie math</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Stat big label="BMR" value={`${estimates.bmr}`} unit="kcal" />
            <Stat big label="TDEE" value={`${estimates.tdee}`} unit="kcal" hint="Maintenance" />
            <Stat big label="Suggested cut" value={`${estimates.suggested_cut_calories}`} unit="kcal" hint="20% deficit" accent />
            <Stat big label="Protein target" value={`${estimates.suggested_protein_g}`} unit="g/day" hint="2g per kg" />
          </div>
          <p className="text-zinc-500 text-xs mt-3 font-manrope">
            Mifflin-St Jeor formula × activity factor ({ACTIVITY_LABEL[prefs?.activity_level] || "—"}).
            Treat as a starting point — adjust based on real-world progress weekly.
          </p>
        </div>
      ) : (
        <div className="border border-zinc-700 bg-[#0F0F0F] px-4 py-3 mb-6 text-zinc-400 text-xs font-manrope" data-testid="brief-tdee-missing">
          Calorie estimate unavailable — client hasn&apos;t finished nutrition onboarding yet (missing age / activity / weight).
        </div>
      )}

      {/* Red flags */}
      {(allergies || avoids) && (
        <div className="mb-6" data-testid="brief-flags">
          <div className="id-label mb-2 text-[#FF3333] flex items-center gap-2">
            <Warning size={14} weight="bold" /> Avoid in plan
          </div>
          <div className="flex flex-wrap gap-2">
            {allergies && (
              <span className="px-3 py-1.5 border border-[#FF3333] bg-[#FF3333]/10 text-xs font-manrope text-white">
                <span className="text-[#FF3333] font-bold mr-1">Allergy:</span> {allergies}
              </span>
            )}
            {avoids && (
              <span className="px-3 py-1.5 border border-zinc-700 bg-zinc-800/50 text-xs font-manrope text-zinc-200">
                <span className="text-zinc-400 font-bold mr-1">Hates:</span> {avoids}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Preferences grid */}
      <div className="border-t border-[#262626] pt-5">
        <div className="id-label mb-4 text-zinc-400">Their food world</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
          <KV label="Age" value={prefs?.age ? `${prefs.age}` : "—"} />
          <KV label="Activity" value={ACTIVITY_LABEL[prefs?.activity_level] || "—"} />
          <KV label="Dietary style" value={prefs?.dietary_style === "other" ? (prefs?.dietary_style_other || "Other") : (DIETARY_LABEL[prefs?.dietary_style] || "—")} />
          <KV label="Meals/day" value={prefs?.meals_per_day ? (prefs.meals_per_day === "if" ? "IF (custom)" : prefs.meals_per_day) : "—"} />
          <KV label="Cook time" value={COOKTIME_LABEL[prefs?.cooking_time_per_day] || "—"} />
          <KV label="Cook confidence" value={prefs?.cooking_confidence ? `${prefs.cooking_confidence}/5` : "—"} />
          <KV label="Alcohol" value={ALCOHOL_LABEL[prefs?.alcohol_intake] || "—"} />
          <KV label="Occupation" value={profile?.occupation || "—"} />
          <KV label="Trains at" value={profile?.training_location ? profile.training_location.toUpperCase() : "—"} />
          <KV label="Supplements" value={prefs?.supplements || "—"} />
          <KV label="Medications" value={prefs?.medications || "—"} />
        </div>

        {(profile?.training_location === "home" || profile?.training_location === "both") && (
          <div className="mt-5">
            <div className="id-label mb-2 text-zinc-400">Home gear</div>
            <div className="flex flex-wrap gap-1.5" data-testid="brief-equipment">
              {(profile?.home_equipment || []).map((e) => (
                <span key={e} className="px-2.5 py-1 border border-[#262626] bg-[#0F0F0F] text-xs font-manrope text-zinc-200">{e}</span>
              ))}
              {profile?.home_equipment_other && (
                <span className="px-2.5 py-1 border border-[#262626] bg-[#0F0F0F] text-xs font-manrope text-zinc-300">+ {profile.home_equipment_other}</span>
              )}
              {(!profile?.home_equipment || profile.home_equipment.length === 0) && !profile?.home_equipment_other && (
                <span className="text-zinc-600 text-xs font-manrope">Not specified</span>
              )}
            </div>
          </div>
        )}

        {prefs?.typical_day_eating && (
          <div className="mt-5">
            <div className="id-label mb-2 text-zinc-400 flex items-center gap-2">
              <ListChecks size={12} weight="bold" /> Typical day right now
            </div>
            <p className="text-zinc-300 text-sm font-manrope whitespace-pre-wrap leading-relaxed border-l-2 border-[#262626] pl-3">
              {prefs.typical_day_eating}
            </p>
          </div>
        )}
      </div>

      {/* Wellness summary */}
      {well?.samples > 0 && (
        <div className="border-t border-[#262626] pt-5 mt-5">
          <div className="id-label mb-3 text-zinc-400 flex items-center gap-2">
            <Lightning size={12} weight="bold" /> Last 14 days wellness average · {well.samples} check-ins
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Cell tiny label="Sleep" value={well.sleep != null ? `${well.sleep}/10` : "—"} testid="brief-sleep" />
            <Cell tiny label="Energy" value={well.energy != null ? `${well.energy}/10` : "—"} testid="brief-energy" />
            <Cell tiny label="Stress" value={well.stress_levels != null ? `${well.stress_levels}/10` : "—"} testid="brief-stress" />
          </div>
          {well.energy != null && well.energy < 5 && (
            <p className="text-xs text-amber-500 mt-3 font-manrope flex items-center gap-1.5">
              <Flame size={12} weight="bold" /> Low energy → consider less aggressive deficit first.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Cell({ label, value, hint, accent, tiny, testid }) {
  return (
    <div className={`border ${accent ? "border-[#FF3333]" : "border-[#262626]"} px-3 ${tiny ? "py-2" : "py-3"}`} data-testid={testid}>
      <div className="text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-bold">{label}</div>
      <div className={`font-oswald ${tiny ? "text-lg" : "text-2xl"} font-bold ${accent ? "text-[#FF3333]" : "text-white"}`}>{value}</div>
      {hint && <div className="text-[10px] text-zinc-600 mt-0.5 font-manrope">{hint}</div>}
    </div>
  );
}

function Stat({ label, value, unit, hint, accent }) {
  return (
    <div className="px-1">
      <div className="text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-bold mb-0.5">{label}</div>
      <div className={`font-oswald text-3xl font-bold leading-none ${accent ? "text-[#FF3333]" : "text-white"}`}>
        {value}
        <span className="text-zinc-500 text-xs font-manrope font-normal ml-1">{unit}</span>
      </div>
      {hint && <div className="text-[10px] text-zinc-500 mt-1 font-manrope">{hint}</div>}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] text-zinc-600 uppercase tracking-[0.15em] font-bold">{label}</div>
      <div className="text-sm text-zinc-200 font-manrope">{value}</div>
    </div>
  );
}
